import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  class: /*@__PURE__*/_normalizeClass(['offer__container'])
}

import type { SearchFormDataType } from '@interfaces/search-form';
import { computed, onMounted, provide, ref, watchEffect } from 'vue';
import type { MergedOfferItemData, VerifiedFlightSegment, AlternativeFlightsDataArray } from '@interfaces/offer';
import type { ClientType, TourOperator } from '@/interfaces/common';
import { useStore } from '@/components/common/store';
import CheckoutInfoBoxStatus from '@lmt-rpb/CheckoutInfoBoxStatus/CheckoutInfoBoxStatus.vue';
import OfferVerifiedBody from './internals/OfferVerifiedBody.vue';
import AlternativeFlights from '@lmt-rpb/AlternativeFlights/AlternativeFlights.vue';
import { useBaseModal } from '@helper/composables/useBaseModal';
import type { OfferListTypoHotelData } from '@interfaces/offer-list-types/offer-list-typo-hotel-data';
import { getFlightSegments, getUnverifiedFlight, updateFlightSegments, generateCheckoutUrl } from './services/flightService';
import { fetchAlternativeFlights } from '@/components/common/services/alternativeFlightsService';
import Loading from '@lmt-rpb/Loading/Loading.vue';
import { OFFER_VERIFIED_INJECTION_KEY, type OfferVerifiedInjectionProps } from './injectionProps';

interface Props {
	offerData: MergedOfferItemData;
	client?: ClientType;
	hotelData: OfferListTypoHotelData;
	isVerified?: boolean;
	autoDetails?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OfferVerified',
  props: {
    offerData: {},
    client: { default: 'at' },
    hotelData: {},
    isVerified: { type: Boolean, default: true },
    autoDetails: { type: Boolean, default: false }
  },
  emits: ['verify-offer'],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const store = useStore();
const [openAlternativeFlightsModal] = useBaseModal('alternative-flight');
const alternativeFlights = ref<AlternativeFlightsDataArray | false>(false);
const alternativeFlightsLoading = ref(false);
const isPackage = computed(() => props.offerData.type === 'package');

const formData = computed((): SearchFormDataType => store.state.searchMask);
const unavailable = computed(() => props.offerData.Status === 'NotAvailable' || (!props.offerData.available && isVerified.value));
const status = computed(() => (unavailable.value ? 'NotAvailable' : props.offerData.Status));

// Get flight segments data
const { first: firstOutboundFlightSegment, last: lastOutboundFlightSegment } = getFlightSegments(props.offerData.OutboundFlightSegments);
const { first: firstInboundFlightSegment, last: lastInboundFlightSegment } = getFlightSegments(props.offerData.InboundFlightSegments);
// Get unverified flight data
const unverifiedOutboundFlight = getUnverifiedFlight(props.offerData.OutboundFlight);
const unverifiedInboundFlight = getUnverifiedFlight(props.offerData.InboundFlight);
const loading = computed((): boolean => props.offerData.loading || false);
const isVerified = computed((): boolean => props.isVerified || false);

const blockClassMofifiers = computed(() => {
	return {
		'offer-verified--unverified': !isVerified.value,
		'offer-verified--unavailable': unavailable.value,
	};
});

provide<OfferVerifiedInjectionProps>(OFFER_VERIFIED_INJECTION_KEY, {
	isFetching: loading,
	isVerified,
	isNotAvailable: unavailable,
	isPackage,
});

// Convert hotelData to the format expected by OfferVerifiedBody
const formattedHotelData = computed(() => {
	return {
		Hotel: {
			IffCode: String(props.hotelData.Hotel.IffCode),
		},
	};
});

const ibeCheckoutUrl = computed((): string => {
	if (!props.isVerified || unavailable.value) return '';

	return generateCheckoutUrl(
		props.offerData,
		props.hotelData,
		{
			firstOutbound: firstOutboundFlightSegment,
			lastOutbound: lastOutboundFlightSegment,
			firstInbound: firstInboundFlightSegment,
			lastInbound: lastInboundFlightSegment,
		},
		{
			outbound: unverifiedOutboundFlight,
			inbound: unverifiedInboundFlight,
		}
	);
});

const operator = computed((): TourOperator => {
	const { Code, Name, Image } = props.offerData.TourOperator;
	const url = 'https://cluster2.images.traveltainment.eu/images/content/va_logos/cp/small';

	return {
		Name,
		Code,
		Image: Image || `${url}/${Code}.gif`,
	};
});
const inboundFlightSegments = computed((): VerifiedFlightSegment[] =>
	updateFlightSegments(props.offerData.InboundFlightSegments, unverifiedInboundFlight)
);

const outboundFlightSegments = computed((): VerifiedFlightSegment[] =>
	updateFlightSegments(props.offerData.OutboundFlightSegments, unverifiedOutboundFlight)
);

const getAlternativeFlights = async (): Promise<void> => {
	alternativeFlightsLoading.value = true;
	try {
		const result = await fetchAlternativeFlights(props.offerData, props.hotelData, formData.value);
		alternativeFlights.value = result;
	} finally {
		alternativeFlightsLoading.value = false;
	}
};

onMounted(() => {
	if (props.autoDetails) {
		emit('verify-offer');
	}
});

watchEffect(async () => {
	if (props.isVerified && isPackage.value && !unavailable.value) {
		await getAlternativeFlights();
	}
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(CheckoutInfoBoxStatus, {
    class: _normalizeClass(['offer-verified', blockClassMofifiers.value]),
    status: status.value,
    "is-verified": _ctx.offerData.availableChecked,
    "bar-classes": "offer-verified__top-bar"
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (loading.value)
          ? (_openBlock(), _createBlock(Loading, {
              key: 0,
              size: "small",
              class: "offer__loading"
            }))
          : _createCommentVNode("v-if", true),
        _createVNode(OfferVerifiedBody, {
          class: _normalizeClass(['offer__body', { 'is-loading': loading.value }]),
          "offer-data": _ctx.offerData,
          client: _ctx.client,
          operator: operator.value,
          "outbound-flight-segments": outboundFlightSegments.value,
          "inbound-flight-segments": inboundFlightSegments.value,
          "hotel-data": formattedHotelData.value,
          "alternative-flights-loading": alternativeFlightsLoading.value,
          "alternative-flights": alternativeFlights.value,
          "ibe-checkout-url": ibeCheckoutUrl.value,
          "is-verified": isVerified.value,
          onShowAlternativeFlights: _unref(openAlternativeFlightsModal),
          onVerifyOffer: _cache[0] || (_cache[0] = ($event: any) => (emit('verify-offer')))
        }, null, 8 /* PROPS */, ["class", "offer-data", "client", "operator", "outbound-flight-segments", "inbound-flight-segments", "hotel-data", "alternative-flights-loading", "alternative-flights", "ibe-checkout-url", "is-verified", "onShowAlternativeFlights"])
      ]),
      _createVNode(AlternativeFlights, {
        ref: "alternative-flight",
        "offer-data": _ctx.offerData,
        "alternative-flights": alternativeFlights.value,
        "checkout-url": ibeCheckoutUrl.value
      }, null, 8 /* PROPS */, ["offer-data", "alternative-flights", "checkout-url"])
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["class", "status", "is-verified"]))
}
}

})